/* eslint-disable no-unused-vars */
import { Typography } from '@mui/material';
import debounce from 'lodash.debounce';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PackageSearchControls from '../components/package_manager/PackageSearchControls';
import PackageTable from '../components/package_manager/PackageTable';
import PaginatedControls from '../components/package_manager/PaginatedControls';
import SearchQuerySummary from '../components/package_manager/SearchQuerySummary';
import packageManagerService from '../services/package-manager-service';
export default function PackageManagerPage() {
  const [packages, setPackages] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState('default');
  const [sortOrder, setSortOrder] = useState('default');
  const [dateFilter, setDateFilter] = useState('default');
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [filterDraft, setFilterDraft] = useState({ field: '', value: '' });
  const [filters, setFilters] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    limit: 20
  });

  useEffect(() => {
    const savedSearch = localStorage.getItem('searchValue');
    const savedFilters = localStorage.getItem('advancedSearchFilters');
    if (savedSearch) setSearchValue(savedSearch);
    if (savedFilters) setFilters(JSON.parse(savedFilters));
  }, []);

  const fetchAllPackages = useCallback(
    async (page = 1, limit = pagination.limit) => {
      setLoading(true);
      try {
        const response = await packageManagerService.getPackageDetailsList(
          page,
          limit
        );
        if (response.success) {
          setPackages(response.data);
          setPagination({
            ...response.pagination,
            limit,
            total: response.total
          });
        } else {
          setPackages([]);
          console.error(response.message);
        }
      } catch (error) {
        console.error('Failed to fetch packages:', error);
      }
      setLoading(false);
    },
    [pagination.limit]
  );

  useEffect(() => {
    fetchAllPackages();
  }, [fetchAllPackages]);

  useEffect(() => {
    localStorage.setItem('searchValue', searchValue);
  }, [searchValue]);

  useEffect(() => {
    localStorage.setItem('advancedSearchFilters', JSON.stringify(filters));
  }, [filters]);

  const handleSearch = useCallback(
    async (page = 1, limit = pagination.limit) => {
      setLoading(true);
      try {
        const response =
          await packageManagerService.fetchAndProcessPackageSearch({
            searchValue,
            filters,
            dateFilter,
            sortBy,
            sortOrder,
            page,
            limit
          });

        if (response.success) {
          setPackages(response.data);
          setPagination({
            ...response.pagination,
            limit,
            total: response.total
          });
        } else {
          setPackages([]);
          console.error(response.message);
        }
      } catch (error) {
        console.error('Failed to fetch packages:', error);
      }
      setLoading(false);
    },
    [searchValue, filters, dateFilter, sortBy, sortOrder, pagination.limit]
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((page, limit) => {
        handleSearch(page, limit);
      }, 500),
    [handleSearch]
  );

  useEffect(() => {
    if (
      searchValue ||
      filters.length > 0 ||
      dateFilter !== 'default' ||
      sortBy !== 'default' ||
      sortOrder !== 'default'
    ) {
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
      debouncedSearch(1, pagination.limit);
    }
  }, [
    searchValue,
    filters,
    dateFilter,
    sortBy,
    sortOrder,
    pagination.limit,
    debouncedSearch
  ]);

  const handleSearchClick = () => {
    const resetPage = 1;
    setPagination((prev) => ({ ...prev, currentPage: resetPage }));
    handleSearch(resetPage);
  };

  const addFilter = () => {
    if (
      filterDraft.field &&
      filterDraft.value &&
      !filters.some((f) => f.field === filterDraft.field)
    ) {
      setFilters((prev) => [...prev, filterDraft]);
      setFilterDraft({ field: '', value: '' });
    }
  };

  const removeFilter = (field) => {
    setFilters((prev) => prev.filter((f) => f.field !== field));
  };

  const formatFieldLabel = (field) => {
    switch (field) {
      case 'direction_type':
        return 'Direction Type';
      case 'recipient_name':
        return 'Recipient Name';
      case 'barcode_list':
        return 'Barcode';
      case 'order_number':
        return 'Order Number';
      case 'uploaded_by':
        return 'Uploaded By';
      default:
        return field;
    }
  };

  const filterOptions = [
    { value: 'direction_type', label: 'Direction Type' },
    { value: 'recipient_name', label: 'Recipient Name' },
    { value: 'barcode_list', label: 'Barcode' },
    { value: 'order_number', label: 'Order Number' },
    { value: 'uploaded_by', label: 'Uploaded By' }
  ];

  const sortByList = [
    { value: 'id', label: 'ID' },
    { value: 'direction_type', label: 'Direction Type' },
    { value: 'company_name', label: 'Company' },
    { value: 'tracking_number', label: 'Tracking Number' },
    { value: 'createdAt', label: 'Created At' }
  ];

  return (
    <div>
      {/* Add spacing so content isn’t hidden behind AppBar */}
      <div style={{ marginTop: 40, padding: 20 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          Package Manager Page
        </Typography>
        <div style={{ margin: '20px auto', width: '80%' }}>
          <PackageSearchControls
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            sortByList={sortByList}
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            dateFilter={dateFilter}
            setDateFilter={setDateFilter}
            filters={filters}
            setFilters={setFilters}
            filterDraft={filterDraft}
            setFilterDraft={setFilterDraft}
            showAdvancedSearch={showAdvancedSearch}
            setShowAdvancedSearch={setShowAdvancedSearch}
            handleSearchClick={handleSearchClick}
            fetchAllPackages={fetchAllPackages}
            setPagination={setPagination}
            filterOptions={filterOptions}
            addFilter={addFilter}
            removeFilter={removeFilter}
            formatFieldLabel={formatFieldLabel}
          />

          {(searchValue || filters.length > 0 || dateFilter !== 'default') && (
            <SearchQuerySummary
              searchValue={searchValue}
              filters={filters}
              dateFilter={dateFilter}
              formatFieldLabel={formatFieldLabel}
            />
          )}

          <PaginatedControls
            pagination={pagination}
            setPagination={setPagination}
            packagesLength={packages.length}
            searchValue={searchValue}
            filters={filters}
            handleSearch={handleSearch}
            fetchAllPackages={fetchAllPackages}
          />

          {loading ? <p>Loading...</p> : <PackageTable packages={packages} />}

          <PaginatedControls
            pagination={pagination}
            setPagination={setPagination}
            packagesLength={packages.length}
            searchValue={searchValue}
            filters={filters}
            handleSearch={handleSearch}
            fetchAllPackages={fetchAllPackages}
          />
        </div>
      </div>
    </div>
  );
}
